import * as React from "react";
import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
  GridSortModel,
  // GridSortModel,
  GridValueFormatterParams,
  GridValueGetterParams,
} from "@mui/x-data-grid";
import "./styles.scss";
import { Delete } from "@mui/icons-material";
import { formatToMonthYear } from "../../utils/dateFormat";
import { BookType } from "../../types/booksType";
import TableFilters from "./TableFilters";
import { currencyFormatter } from "../../utils/utils";
import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Backdrop,
  CircularProgress
} from "@mui/material";

type BooksTableProps = {
  data: Array<BookType>;
  search: string;
  deleteTransaction: Function;
  clearSearch: Function;
  // eslint-disable-next-line no-unused-vars
  onSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

const BooksTable = (props: BooksTableProps) => {
  const { data, deleteTransaction } = props;
  const [filteredData, setFilteredData] = useState(data);
  const [selectedYear, setSelectedYear] = useState("All");
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: "date", sort: "desc" },
  ]);
  const [confirm, setConfirm] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  const deletableRecord = (currId: number | null): boolean => {
    if (!currId) {
      return false;
    }

    let mostRecentId: number | null = null;
    let mostRecentYear = -Infinity;
    let mostRecentMonth = -1;

    data.forEach((item) => {
      if (item.year > mostRecentYear || (item.year === mostRecentYear && item.month > mostRecentMonth)) {
        mostRecentYear = item.year;
        mostRecentMonth = item.month;
        mostRecentId = item.id;
      }
    });

    if (mostRecentId === null) {
      return false;
    }

    if (currId !== mostRecentId) {
      return false;
    }

    return true;
  };

  const handleCloseConfirm = () => setConfirm(null);

  const onConfirm = () => {
    if (confirm) {
      setLoading(true);
      deleteTransaction(confirm);
      handleCloseConfirm();
    }
  };

  useEffect(() => {
    if (selectedYear === "All") {
      setFilteredData(data);
    } else {
      setFilteredData(
        data.filter((item) => item.year === parseInt(selectedYear))
      );
    }
  }, [selectedYear]);

  const columns: GridColDef[] = React.useMemo<GridColDef[]>(
    () => [
      {
        field: "date",
        headerName: "Month",
        minWidth: 150,
        sortable: true,
        valueGetter: (params: GridValueGetterParams) =>
          formatToMonthYear(params.row.month, params.row.year),
        sortComparator: (v1, v2) => {
          const [month1, year1] = v1.toString().split(" ");
          const [month2, year2] = v2.toString().split(" ");
          const monthMap: Record<string, number> = {
            JAN: 0,
            FEB: 1,
            MAR: 2,
            APR: 3,
            MAY: 4,
            JUN: 5,
            JUL: 6,
            AUG: 7,
            SEP: 8,
            OCT: 9,
            NOV: 10,
            DEC: 11,
          };
          const date1 = new Date(parseInt(year1), monthMap[month1], 1);
          const date2 = new Date(parseInt(year2), monthMap[month2], 1);
          return date1.getTime() - date2.getTime();
        },
        headerClassName: "header-datagrid",
      },
      {
        field: "investmentRate",
        headerName: "Inv. Rate",
        minWidth: 150,
        sortable: false,
        headerClassName: "header-datagrid",
        valueFormatter: (params: GridValueFormatterParams) =>
          `${params.value}%`,
      },
      {
        field: "loanRate",
        headerName: "Loan rate",
        minWidth: 150,
        sortable: false,
        headerClassName: "header-datagrid",
        valueFormatter: (params: GridValueFormatterParams) =>
          `${params.value}%`,
      },
      {
        field: "managementFee",
        headerName: "Mgmt. Fee",
        minWidth: 150,
        sortable: false,
        headerClassName: "header-datagrid",
        valueFormatter: (params: GridValueFormatterParams) =>
          currencyFormatter.format(params.value as number),
      },
      {
        field: "managementRate",
        headerName: "Mgmt. Rate",
        minWidth: 150,
        sortable: false,
        headerClassName: "header-datagrid",
        valueFormatter: (params: GridValueFormatterParams) =>
          `${params.value}%`,
      },
      {
        field: "description",
        headerName: "Description",
        minWidth: 300,
        sortable: false,
        headerClassName: "header-datagrid",
      },
      {
        field: "delete",
        headerName: "",
        sortable: false,
        maxWidth: 60,
        align: "center",
        headerAlign: "center",
        headerClassName: "header-datagrid",
        type: "actions",
        getActions: (params: GridValueGetterParams) => {
          if (deletableRecord(params.row.id)) {
            return [
              <GridActionsCellItem
                icon={<Delete sx={{ fontSize: "20px" }} />}
                label=""
                onClick={() => {
                  setConfirm(params.row.id);
                }}
              />,
            ];
          } else return [];
        },
      },
    ],
    []
  );

  return (
    <>
      <div className="data-table">
        <TableFilters
          selectedYear={selectedYear}
          setSelectedYear={setSelectedYear}
        />
        <DataGrid
          rows={filteredData}
          getRowId={(row) => row.id}
          columns={columns}
          sortModel={sortModel}
          onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 },
            },
          }}
          pageSizeOptions={[10, 20, 30, 40, 50]}
          rowSelection={false}
          className="datagrid"
        />

        {confirm && (
          <Dialog
            open={true}
            onClose={handleCloseConfirm}
            aria-labelledby="draggable-dialog-title"
          >
            <DialogTitle style={{ cursor: "move" }} id="draggable-dialog-title">
            Are You Sure You Want to Delete Close Book?
            </DialogTitle>
            <DialogContent>
              <DialogContentText>
              Are you sure you want to delete the Close Books?
              Please note: Deleting Close Books will reset the status of all transactions within this period to 'Outstanding’.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button color="inherit" autoFocus onClick={handleCloseConfirm}>
              Cancel
              </Button>
              <Button variant="contained" onClick={onConfirm}>
              Yes, Delete
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </div>
      <Backdrop
        sx={{ color: '#fff', zIndex: theme => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
};

export default BooksTable;
